import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

export const uid = () =>
  window.crypto?.randomUUID ? window.crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const SECTION_TYPE_LABEL = {
  hero: "Hero", rich_text: "Text", image_text: "Image + Text", cta_band: "CTA Band",
  feature_grid: "Feature Grid", gallery: "Gallery", embed: "Functional",
};

export const newSection = (type) => ({
  id: uid(),
  type,
  label: SECTION_TYPE_LABEL[type] || "Section",
  visible: true,
  content:
    type === "hero"
      ? { eyebrow: "", heading_left: "New ", heading_right: "Section", subhead: "" }
      : { heading: "New Section", body: "" },
  media: { image_url: "", alt: "", focal_x: 50, focal_y: 50, video_url: "" },
  overlay: { type: type === "hero" || type === "cta_band" ? "black" : "none", opacity: 50 },
  buttons: [],
  widget: "",
});

// Loads a page's draft sections and exposes editing operations with draft/live isolation.
export function useVisualEditor(pageKey) {
  const [meta, setMeta] = useState(null);
  const [sections, setSections] = useState([]);
  const [baseVersion, setBaseVersion] = useState(1);
  const [hasDraft, setHasDraft] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/pages/${pageKey}`)
      .then(({ data }) => {
        setMeta(data);
        const base = data.draft_sections ?? data.published_sections ?? [];
        setSections(JSON.parse(JSON.stringify(base)));
        setBaseVersion(data.version || 1);
        setHasDraft(!!data.has_draft);
        setDirty(false);
      })
      .catch(() => toast.error("Could not load page for editing"))
      .finally(() => setLoading(false));
  }, [pageKey]);
  useEffect(() => { load(); }, [load]);

  const mutate = (fn) => { setSections((prev) => { const c = JSON.parse(JSON.stringify(prev)); fn(c); return c; }); setDirty(true); };
  const at = (c, id) => c.findIndex((s) => s.id === id);

  const patch = (id, group, value) =>
    mutate((c) => {
      const i = at(c, id); if (i < 0) return;
      if (group) c[i][group] = { ...(c[i][group] || {}), ...value };
      else Object.assign(c[i], value);
    });
  const move = (id, dir) => mutate((c) => { const i = at(c, id); const j = i + dir; if (j < 0 || j >= c.length) return; const [it] = c.splice(i, 1); c.splice(j, 0, it); });
  const reorder = (from, to) => mutate((c) => { if (to < 0 || to >= c.length) return; const [it] = c.splice(from, 1); c.splice(to, 0, it); });
  const duplicate = (id) => mutate((c) => { const i = at(c, id); const cp = JSON.parse(JSON.stringify(c[i])); cp.id = uid(); cp.label = `${cp.label || "Section"} (copy)`; c.splice(i + 1, 0, cp); });
  const remove = (id) => mutate((c) => { const i = at(c, id); if (i >= 0) c.splice(i, 1); });
  const toggleVisible = (id) => mutate((c) => { const i = at(c, id); if (i >= 0) c[i].visible = c[i].visible === false; });
  const addSection = (type) => { const s = newSection(type); mutate((c) => c.push(s)); return s.id; };

  const saveDraft = async () => {
    setSaving(true);
    try { await api.put(`/admin/pages/${pageKey}/draft`, { sections }); setDirty(false); setHasDraft(true); toast.success("Draft saved"); return true; }
    catch (e) { toast.error("Could not save draft"); return false; }
    finally { setSaving(false); }
  };
  const commit = async () => {
    setSaving(true);
    try {
      if (dirty) await api.put(`/admin/pages/${pageKey}/draft`, { sections });
      const { data } = await api.post(`/admin/pages/${pageKey}/commit`, { base_version: baseVersion });
      toast.success(`Published live (v${data.version})`);
      load();
      return true;
    } catch (e) {
      if (e?.response?.status === 409) toast.error("This page changed elsewhere — reload before committing.", { duration: 6000 });
      else if (e?.response?.status === 400) toast.error("Make a change first, then commit.");
      else toast.error(e?.response?.data?.detail || "Commit failed — live page unchanged.");
      return false;
    } finally { setSaving(false); }
  };
  const discard = async () => {
    try { await api.post(`/admin/pages/${pageKey}/discard`); toast.success("Draft discarded"); load(); }
    catch (e) { toast.error("Could not discard draft"); }
  };

  return {
    meta, sections, loading, dirty, saving, hasDraft, baseVersion,
    patch, move, reorder, duplicate, remove, toggleVisible, addSection,
    saveDraft, commit, discard, reload: load,
  };
}
